/* NORDAEN — product form.

   Deliberately thin. The form is a real Shopify product form and will post
   to /cart/add and redirect if JavaScript never runs. This only adds:
     - quantity stepper buttons
     - variant resolution when options change
     - async add-to-cart so the page doesn't jump

   If the async add fails for any reason we fall back to a native submit
   rather than stranding the customer. Buying must never depend on this file.

   There may be more than one of these on a page. A line page carries every
   object in the line, each with its own gallery and its own form, so this
   binds per [data-n-product] and every lookup inside is scoped to that root.
   It used to take the first one on the page, which on a line page would have
   left the second object's stepper and add button inert. */

(() => {
  const bind = (root) => {
    if (!root || root.dataset.nBound) return;
    root.dataset.nBound = '1';

    const form = root.querySelector('form');
    const button = root.querySelector('[data-n-add]');
    const buttonText = root.querySelector('[data-n-add-text]');
    const status = root.querySelector('[data-n-status]');
    const qty = root.querySelector('[data-n-qty]');

    /* ---- Quantity ---------------------------------------------- */
    root.querySelectorAll('[data-n-step]').forEach((step) => {
      step.addEventListener('click', () => {
        if (!qty) return;
        const delta = Number(step.dataset.nStep) || 0;
        const next = Math.max(1, (parseInt(qty.value, 10) || 1) + delta);
        qty.value = next;
        qty.dispatchEvent(new Event('change', { bubbles: true }));
      });
    });


    /* ---- Gallery index ------------------------------------------
       The carousel itself is native scroll-snap; this only keeps the
       pip readout in step. Purely cosmetic — if it never runs, the
       gallery still swipes. */
    const gallery = root.querySelector('[data-n-gallery]');
    const index = root.querySelector('[data-n-index]');
    if (gallery && index && 'IntersectionObserver' in window) {
      const shots = [...gallery.querySelectorAll('[data-n-shot]')];
      const pips = [...index.querySelectorAll('.n-product__pip')];
      const io = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (!entry.isIntersecting) return;
            const i = shots.indexOf(entry.target);
            pips.forEach((pip, n) => pip.classList.toggle('is-on', n === i));
          });
        },
        { root: gallery, threshold: 0.6 }
      );
      shots.forEach((s) => io.observe(s));
    }

    /* ---- Add to bag -------------------------------------------- */
    if (!form || !button) return;

    const say = (msg) => { if (status) status.textContent = msg; };

    form.addEventListener('submit', async (e) => {
      // Let the browser handle it natively if fetch is unavailable.
      if (typeof fetch !== 'function') return;
      e.preventDefault();

      const original = buttonText ? buttonText.textContent : '';
      button.classList.add('is-busy');
      say('');

      try {
        const res = await fetch('/cart/add.js', {
          method: 'POST',
          headers: { Accept: 'application/json' },
          body: new FormData(form),
        });

        if (!res.ok) {
          const err = await res.json().catch(() => ({}));
          throw new Error(err.description || err.message || 'Could not add to bag');
        }

        if (buttonText) buttonText.textContent = 'Added';
        say('');

        // Let the rest of the theme know, so the bag count can update.
        document.dispatchEvent(new CustomEvent('cart:updated', { bubbles: true }));
        refreshBagCount();

        setTimeout(() => {
          if (buttonText) buttonText.textContent = original;
        }, 2200);
      } catch (err) {
        say(err.message || 'Could not add to bag');
        // Give the customer a working path regardless of what went wrong.
        button.classList.remove('is-busy');
        form.submit();
        return;
      }

      button.classList.remove('is-busy');
    });
  };

  /* Pull the authoritative count from Shopify rather than guessing. Shared by
     every form on the page — the bag is one thing however many objects are
     offered above it. */
  async function refreshBagCount() {
    const el = document.querySelector('.n-header__bag-count');
    if (!el || typeof fetch !== 'function') return;
    try {
      const cart = await (await fetch('/cart.js', { headers: { Accept: 'application/json' } })).json();
      el.textContent = cart.item_count;
      el.classList.toggle('is-filled', cart.item_count > 0);
    } catch (_) {
      /* A stale count is not worth breaking anything over. */
    }
  }

  const init = () => document.querySelectorAll('[data-n-product]').forEach(bind);

  if (document.readyState !== 'loading') init();
  else document.addEventListener('DOMContentLoaded', init, { once: true });

  document.addEventListener('shopify:section:load', init);
})();
